import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type StaffAction = components['schemas']['StaffActionResponse'];

/**
 * The window this screen opens with. The server's own default when `take` is
 * omitted, so the first load asks for exactly what it would have got anyway.
 */
export const AUDIT_DEFAULT_TAKE = 50;

/**
 * The largest window the API will actually serve.
 *
 * ⚠️ **This endpoint CLAMPS; the tenant list next door REFUSES.**
 * `AdminTenantEndpoints` answers 400 for a `pageSize` over 100, so asking for
 * too much there is a loud error. `StaffAudit.RecentAsync` runs
 * `Math.Clamp(take, 1, 200)` and returns 200 OK, so asking for too much here is
 * a *silent* truncation — the response looks exactly like a complete answer.
 *
 * That is why the screen tracks its own `take` and tells the reader when the
 * two are equal: there is no total count in the response to compare against,
 * so "I received as many rows as I asked for" is the only signal that the trail
 * may go further back. `AdminAuditEndpointTests` pins both halves.
 */
export const AUDIT_MAX_TAKE = 200;

/** Raised when the API refuses the trail outright. */
export class AuditForbiddenError extends Error {
  constructor() {
    super('The API refused the audit trail');
    this.name = 'AuditForbiddenError';
  }
}

export const auditQueryKey = (take: number) => ['admin', 'audit', take] as const;

/**
 * The most recent staff actions, newest first.
 *
 * The ordering is the API's and is not re-derived here: `CreatedAt` descending,
 * then `Id` descending. The tiebreaker matters more than it looks — actions
 * recorded in one `SaveChanges` tie on `CreatedAt` exactly, and without it the
 * database was free to return them in a different order on every call. Ids are
 * `Guid.CreateVersion7()`, so the tiebreaker sorts broadly by creation time,
 * though it promises nothing WITHIN a millisecond. The screen prints timestamps
 * and lets them speak rather than implying more precision than that.
 *
 * Not polled. Unlike the diagnostics list, an audit trail is read deliberately
 * rather than watched, and this console's whole discipline is that a staff read
 * is a recorded event — refetching on a timer would be this screen quietly
 * generating traffic nobody asked for.
 */
export function useAuditTrail(take: number) {
  return useQuery({
    queryKey: auditQueryKey(take),
    queryFn: async (): Promise<StaffAction[]> => {
      const { data, response } = await apiClient.GET('/api/v1/admin/audit', {
        params: { query: { take } },
      });

      // Branched on status, not on `error`: `TypedResults.Forbid()` sends no
      // body and no content-type, so there is nothing for an error-shaped
      // check to find. Reachable even behind the route guard — `/admin/me` is
      // cached five minutes on both sides, so an enrolment changed mid-session
      // leaves a console whose capability set the API no longer honours.
      if (response.status === 403) {
        throw new AuditForbiddenError();
      }

      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/admin/audit failed: ${response.status}`);
      }

      return data;
    },
  });
}

/**
 * Whether the trail probably continues past what came back.
 *
 * A guess, and deliberately worded as one on screen. The response carries no
 * total, so the only evidence is having received exactly as many rows as were
 * asked for — which is also what a trail of exactly that length looks like.
 * Over-reporting "there may be more" is the safe direction for an audit screen:
 * the failure to avoid is someone concluding they have seen everything.
 */
export function mayHaveMore(rows: readonly StaffAction[], take: number): boolean {
  return rows.length >= take;
}
